import { useState } from 'react';
import type { PatchNote, PatchNoteDraft, PatchChange } from '../../../types/patchNote';

interface PatchNoteEditorProps {
  initial?: PatchNote;
  onSubmit: (data: PatchNoteDraft, publish: boolean) => void;
  onCancel: () => void;
  submitting: boolean;
}

const EMPTY_SECTION: PatchChange = { area: '', changes: [''] };

function SectionEditor({
  section,
  index,
  onChange,
  onRemove,
  accent,
}: {
  section: PatchChange;
  index: number;
  onChange: (updated: PatchChange) => void;
  onRemove: () => void;
  accent: string;
}) {
  const updateChange = (i: number, val: string) => {
    const updated = [...section.changes];
    updated[i] = val;
    onChange({ ...section, changes: updated });
  };

  const addChange = () => onChange({ ...section, changes: [...section.changes, ''] });

  const removeChange = (i: number) => {
    const updated = section.changes.filter((_, ci) => ci !== i);
    onChange({ ...section, changes: updated.length ? updated : [''] });
  };

  return (
    <div className="border border-[#2a2a2a] rounded-lg p-4 bg-[#0d0d0d]">
      <div className="flex items-center justify-between mb-3">
        <span className={`font-['Barlow_Condensed'] font-black text-xs uppercase tracking-widest ${accent}`}>
          Section {index + 1}
        </span>
        <button
          type="button"
          onClick={onRemove}
          className="w-6 h-6 flex items-center justify-center text-[#555] hover:text-red-400 transition-colors cursor-pointer"
        >
          <i className="ri-close-line text-sm" />
        </button>
      </div>

      {/* Area label */}
      <input
        type="text"
        value={section.area}
        onChange={(e) => onChange({ ...section, area: e.target.value })}
        placeholder="Section title (e.g. Assault Rifles)"
        className="w-full bg-[#111] border border-[#2a2a2a] rounded-md px-3 py-2 text-white text-sm font-['Barlow_Condensed'] font-bold uppercase tracking-wider placeholder-[#444] focus:outline-none focus:border-[#444] mb-3"
      />

      {/* Changes */}
      <div className="space-y-2">
        {section.changes.map((change, ci) => (
          <div key={ci} className="flex items-start gap-2">
            <span className="mt-2.5 shrink-0 w-1.5 h-1.5 bg-[#444] rounded-full" />
            <input
              type="text"
              value={change}
              onChange={(e) => updateChange(ci, e.target.value)}
              placeholder="Describe this change..."
              className="flex-1 bg-[#111] border border-[#1f1f1f] rounded-md px-3 py-2 text-[#ccc] text-sm placeholder-[#444] focus:outline-none focus:border-[#444]"
            />
            <button
              type="button"
              onClick={() => removeChange(ci)}
              className="mt-1 w-7 h-7 flex items-center justify-center text-[#444] hover:text-red-400 transition-colors cursor-pointer shrink-0"
            >
              <i className="ri-delete-bin-line text-xs" />
            </button>
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={addChange}
        className="mt-3 flex items-center gap-1 text-[#444] hover:text-[#00ff41] text-xs font-['Barlow_Condensed'] font-bold tracking-wider transition-colors cursor-pointer"
      >
        <i className="ri-add-line" /> ADD CHANGE
      </button>
    </div>
  );
}

export function PatchNoteForm({ initial, onSubmit, onCancel, submitting }: PatchNoteEditorProps) {
  const [version, setVersion] = useState(initial?.version ?? '');
  const [title, setTitle] = useState(initial?.title ?? '');
  const [category, setCategory] = useState<PatchNote['category']>(initial?.category ?? 'Warzone');
  const [highlight, setHighlight] = useState(initial?.highlight ?? '');
  const [date, setDate] = useState(initial?.date ? initial.date.substring(0, 10) : new Date().toISOString().substring(0, 10));
  const [sections, setSections] = useState<PatchChange[]>(
    initial?.sections?.length ? initial.sections : [{ ...EMPTY_SECTION, changes: [''] }]
  );

  const accent = category === 'Warzone' ? 'text-[#ff6b35]' : category === 'Multiplayer' ? 'text-[#00ff41]' : 'text-purple-400';

  const updateSection = (i: number, updated: PatchChange) => {
    setSections((prev) => prev.map((s, si) => (si === i ? updated : s)));
  };

  const removeSection = (i: number) => {
    setSections((prev) => prev.filter((_, si) => si !== i));
  };

  const addSection = () => {
    setSections((prev) => [...prev, { area: '', changes: [''] }]);
  };

  const handleSubmit = (publish: boolean) => {
    if (!version.trim() || !title.trim() || !date) return;
    onSubmit({ version, title, category, highlight, date, sections, published: publish }, publish);
  };

  return (
    <form onSubmit={(e) => { e.preventDefault(); handleSubmit(true); }} className="space-y-6">
      {/* Version + Category row */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-gray-400 text-xs font-['Barlow_Condensed'] font-bold tracking-widest uppercase mb-2">
            Version <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={version}
            onChange={(e) => setVersion(e.target.value)}
            placeholder="e.g. 1.43.0"
            required
            className="w-full bg-[#0d0d0d] border border-[#2a2a2a] rounded-md px-4 py-2.5 text-white text-sm font-mono placeholder-[#444] focus:outline-none focus:border-[#444]"
          />
        </div>
        <div>
          <label className="block text-gray-400 text-xs font-['Barlow_Condensed'] font-bold tracking-widest uppercase mb-2">
            Category <span className="text-red-500">*</span>
          </label>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value as PatchNote['category'])}
            className="w-full bg-[#0d0d0d] border border-[#2a2a2a] rounded-md px-4 py-2.5 text-white text-sm focus:outline-none focus:border-[#444] cursor-pointer"
          >
            <option value="Warzone">Warzone</option>
            <option value="Multiplayer">Multiplayer</option>
            <option value="Zombies">Zombies</option>
          </select>
        </div>
      </div>

      {/* Title */}
      <div>
        <label className="block text-gray-400 text-xs font-['Barlow_Condensed'] font-bold tracking-widest uppercase mb-2">
          Title <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g. Season 4 Launch — Ranked Overhaul"
          required
          className="w-full bg-[#0d0d0d] border border-[#2a2a2a] rounded-md px-4 py-2.5 text-white text-sm placeholder-[#444] focus:outline-none focus:border-[#444]"
        />
      </div>

      {/* Highlight + Date row */}
      <div className="grid grid-cols-3 gap-4">
        <div className="col-span-2">
          <label className="block text-gray-400 text-xs font-['Barlow_Condensed'] font-bold tracking-widest uppercase mb-2">
            Summary / Highlight
          </label>
          <input
            type="text"
            value={highlight}
            onChange={(e) => setHighlight(e.target.value)}
            placeholder="Short one-line summary of this patch..."
            className="w-full bg-[#0d0d0d] border border-[#2a2a2a] rounded-md px-4 py-2.5 text-white text-sm placeholder-[#444] focus:outline-none focus:border-[#444]"
          />
        </div>
        <div>
          <label className="block text-gray-400 text-xs font-['Barlow_Condensed'] font-bold tracking-widest uppercase mb-2">
            Date <span className="text-red-500">*</span>
          </label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            required
            className="w-full bg-[#0d0d0d] border border-[#2a2a2a] rounded-md px-4 py-2.5 text-white text-sm focus:outline-none focus:border-[#444] cursor-pointer"
          />
        </div>
      </div>

      {/* Sections */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <label className="text-gray-400 text-xs font-['Barlow_Condensed'] font-bold tracking-widest uppercase">
            Change Sections
          </label>
          <span className={`text-xs font-['Barlow_Condensed'] font-bold ${accent}`}>
            {sections.length} {sections.length === 1 ? 'SECTION' : 'SECTIONS'}
          </span>
        </div>
        <div className="space-y-3">
          {sections.map((section, i) => (
            <SectionEditor
              key={i}
              section={section}
              index={i}
              onChange={(updated) => updateSection(i, updated)}
              onRemove={() => removeSection(i)}
              accent={accent}
            />
          ))}
        </div>
        <button
          type="button"
          onClick={addSection}
          className="mt-4 w-full border border-dashed border-[#2a2a2a] rounded-lg py-3 text-[#444] hover:text-[#00ff41] hover:border-[#00ff41]/30 text-sm font-['Barlow_Condensed'] font-bold tracking-wider transition-all cursor-pointer flex items-center justify-center gap-2"
        >
          <i className="ri-add-circle-line" /> ADD SECTION
        </button>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between pt-4 border-t border-[#1f1f1f]">
        <button
          type="button"
          onClick={onCancel}
          className="text-gray-500 hover:text-white text-sm transition-colors cursor-pointer whitespace-nowrap"
        >
          Cancel
        </button>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => handleSubmit(false)}
            disabled={submitting}
            className="border border-[#2a2a2a] text-gray-400 hover:text-white hover:border-[#444] font-['Barlow_Condensed'] font-bold text-sm tracking-wider px-5 py-2.5 rounded-md transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
          >
            SAVE DRAFT
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="bg-[#00ff41] hover:bg-[#00e63a] text-black font-['Barlow_Condensed'] font-black text-sm tracking-wider px-6 py-2.5 rounded-md transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 flex items-center gap-2"
          >
            {submitting ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-rocket-line" />}
            {submitting ? 'SAVING...' : 'PUBLISH NOW'}
          </button>
        </div>
      </div>
    </form>
  );
}
